import React, { useEffect, useRef } from 'react';
import CampusVideo from '../assets/MDU Campus Tour Ariel view.mp4'; // Placeholder path for the video

const CampusVideoSection = () => {
    const videoRef = useRef(null);

    useEffect(() => {
        // Play video after slight delay for better UX
        const timer = setTimeout(() => {
            if (videoRef.current) {
                videoRef.current.play().catch(err => console.log('Auto-play prevented:', err));
            }
        }, 1000);
        
        return () => clearTimeout(timer);
    }, []);

    return (
        <div className="w-full bg-gradient-to-br from-gray-50 via-white to-green-50 py-20 px-4 md:px-8 relative overflow-hidden">
            {/* Animated Background Elements */}
            <div className="absolute inset-0 opacity-20 pointer-events-none">
                <div className="absolute top-0 left-0 w-96 h-96 bg-green-300 rounded-full blur-3xl animate-pulse-slow"></div>
                <div className="absolute bottom-0 right-0 w-96 h-96 bg-blue-300 rounded-full blur-3xl animate-pulse-slow animation-delay-4000"></div>
                <div className="absolute top-1/3 right-1/4 w-64 h-64 bg-teal-200 rounded-full blur-3xl animate-pulse-slow animation-delay-2000"></div>
            </div>

            {/* Custom Animations and Font Imports */}
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800&family=Plus+Jakarta+Sans:wght@300;400;500;600;700;800&display=swap');
                
                @keyframes pulse-slow {
                    0%, 100% { transform: scale(1); opacity: 0.2; }
                    50% { transform: scale(1.2); opacity: 0.3; }
                }
                @keyframes fade-in {
                    from { opacity: 0; transform: translateY(20px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                @keyframes slide-up {
                    from { opacity: 0; transform: translateY(30px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                @keyframes float {
                    0%, 100% { transform: translateY(0); }
                    50% { transform: translateY(-10px); }
                }
                .animate-pulse-slow {
                    animation: pulse-slow 8s infinite;
                }
                .animate-fade-in {
                    animation: fade-in 0.8s ease-out forwards;
                }
                .animate-slide-up {
                    animation: slide-up 0.8s ease-out forwards;
                }
                .animate-float {
                    animation: float 6s ease-in-out infinite;
                }
                .animation-delay-2000 {
                    animation-delay: 2s;
                }
                .animation-delay-4000 {
                    animation-delay: 4s;
                }
                .font-outfit {
                    font-family: 'Outfit', sans-serif;
                }
                .font-jakarta {
                    font-family: 'Plus Jakarta Sans', sans-serif;
                }
                .text-shadow {
                    text-shadow: 0 2px 10px rgba(0,0,0,0.08);
                }
                .clip-path-slant {
                    clip-path: polygon(0 0, 100% 0, 100% 85%, 0 100%);
                }
            `}</style>

            {/* Main Content */}
            <div className="relative z-10 max-w-7xl mx-auto text-center">
                {/* Heading */}
                <h2 className="font-outfit text-5xl md:text-7xl font-extrabold text-shadow tracking-tight mb-6 animate-fade-in">
                    <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-600 via-teal-500 to-blue-600">
                        Discover Our Campus
                    </span>
                </h2>
                <p className="font-jakarta text-lg md:text-xl text-gray-600 max-w-3xl mx-auto mb-12 leading-relaxed animate-slide-up">
                    Take a breathtaking aerial tour of our state-of-the-art campus, where innovation meets inspiration amidst lush greenery and modern architecture.
                </p>

                {/* Video Player with Enhanced Container */}
                <div className="w-full max-w-5xl mx-auto mb-16 rounded-2xl overflow-hidden shadow-2xl animate-slide-up relative">
                    {/* Video Overlay Elements */}
                    <div className="absolute -top-6 -left-6 w-24 h-24 bg-emerald-500 opacity-20 rounded-full blur-xl animate-pulse-slow"></div>
                    <div className="absolute -bottom-6 -right-6 w-24 h-24 bg-blue-500 opacity-20 rounded-full blur-xl animate-pulse-slow animation-delay-2000"></div>
                    
                    {/* Video */}
                    <video
                        ref={videoRef}
                        className="w-full h-auto max-h-[70vh] object-cover"
                        controls
                        muted
                        loop
                        poster="/images/campus-video-poster.jpg" // Add a placeholder poster image
                        aria-label="Aerial view of university campus"
                    >
                        <source src={CampusVideo} type="video/mp4" />
                        Your browser does not support the video tag.
                    </video>
                </div>

                {/* Call to Action */}
                <div className="mt-8 animate-float">
                    <a
                        href="/admission/infrastructure"
                        className="font-jakarta inline-flex items-center px-8 py-4 bg-gradient-to-r from-emerald-600 to-blue-600 text-white font-semibold text-lg tracking-wide rounded-lg hover:from-emerald-700 hover:to-blue-700 transition-all duration-300 shadow-lg animate-slide-up"
                    >
                        Explore Campus Life
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                        </svg>
                    </a>
                </div>
                
                {/* Features Section */}
                <div className="mt-24 grid grid-cols-1 md:grid-cols-3 gap-8">
                    <div className="bg-white bg-opacity-70 backdrop-blur-sm p-6 rounded-xl shadow-lg animate-slide-up">
                        <h3 className="font-outfit text-xl font-bold text-emerald-700 mb-3">Modern Facilities</h3>
                        <p className="font-jakarta text-gray-600">State-of-the-art laboratories, libraries, and learning spaces designed for the future.</p>
                    </div>
                    <div className="bg-white bg-opacity-70 backdrop-blur-sm p-6 rounded-xl shadow-lg animate-slide-up" style={{animationDelay: '0.2s'}}>
                        <h3 className="font-outfit text-xl font-bold text-emerald-700 mb-3">Green Campus</h3>
                        <p className="font-jakarta text-gray-600">Sustainability at the core with lush gardens, solar power, and eco-friendly architecture.</p>
                    </div>
                    <div className="bg-white bg-opacity-70 backdrop-blur-sm p-6 rounded-xl shadow-lg animate-slide-up" style={{animationDelay: '0.4s'}}>
                        <h3 className="font-outfit text-xl font-bold text-emerald-700 mb-3">Student Life</h3>
                        <p className="font-jakarta text-gray-600">Vibrant community spaces that foster collaboration, creativity, and personal growth.</p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CampusVideoSection;